import { useState, useEffect } from 'react'

import {
	EuiSelectable,
	EuiSelectableOption,
} from '@equipmentshare/ds2'

import { BranchWithInventoryLocations } from 'hooks/inventory-location-search'

export type SelectedItem = {
	id: string,
	name: string,
}

export type InventoryLocationsSelectableProps = {
	branches: BranchWithInventoryLocations[],
	selectedIds: string[],
	setSelectedIds: ( ids: string[] ) => void,
	setSelectedItems?: ( items: SelectedItem[] ) => void,
	style?: React.CSSProperties,
	isLoading?: boolean,
}

export function buildOptions( branches: BranchWithInventoryLocations[], selectedIds: string[] ) {
	const options: EuiSelectableOption[] = []

	for ( const branch of branches ) {
		options.push( {
			label: branch.name,
			isGroupLabel: true,
			data: branch,
		} )
		for ( const location of branch.inventoryLocations ) {
			options.push( {
				label: location.name,
				checked: selectedIds.includes( location.id ) ? 'on' : undefined,
				data: location,
			} )
		}
	}

	return options
}

export function getSelectedIds( options: EuiSelectableOption[] ) {
	const ids: string[] = []
	for ( const option of options ) {
		if ( option.checked === 'on' ) {
			ids.push( option.data?.id )
		}
	}

	return ids
}

export function getSelectedItems( options: EuiSelectableOption[] ) {
	const items: { id: string, name: string }[] = []
	for ( const option of options ) {
		if ( option.checked === 'on' ) {
			items.push( {
				id: option.data?.id ?? '',
				name: option.label,
			} )
		}
	}

	return items
}

export default function InventoryLocationsSelectable( { branches, selectedIds, setSelectedIds, setSelectedItems, ...props }: InventoryLocationsSelectableProps ) {
	const [ options, setOptions ] = useState( buildOptions( branches, selectedIds ) )

	useEffect( () => {
		const newOptions = buildOptions( branches, selectedIds )
		setOptions( newOptions )
	}, [ branches, selectedIds ] )

	const onChange = ( newOptions: EuiSelectableOption[] ) => {
		setOptions( newOptions )
		setSelectedIds( getSelectedIds( newOptions ) )
		setSelectedItems?.( getSelectedItems( newOptions ) )
	}

	return (
		<EuiSelectable
			loadingMessage="Loading..."
			isPreFiltered
			options={options}
			onChange={onChange}
			height="full"
			{...props}
		>
			{( list ) => list}
		</EuiSelectable>
	)
}
